import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import type { AutonomyPage as Page } from '../../api/settingsAutonomy'
import { mailIntakeQuery } from '../../api/settingsMailIntake'
import { n, s, type Level, type Tab } from './AutonomyPage'

type Tone = 'good' | 'warn' | 'bad' | 'off' | 'info'

function Card({ id, title, state, tone, facts, children, open, onOpen, alert }: {
  id: string
  title: string
  state: string
  tone: Tone
  facts: ReactNode[]
  children?: ReactNode
  open: string
  onOpen(): void
  alert?: boolean
}) {
  return (
    <div className={`odx-card${alert ? ' is-alert' : ''}`} id={id}>
      <div className="odx-card-head">
        <strong>{title}</strong>
        {state && <span className={`odx-pill is-${tone}`}>{state}</span>}
      </div>
      {children}
      {facts.filter(Boolean).map((f, i) => <div key={i} className="odx-card-fact">{f}</div>)}
      <button type="button" className="odx-card-open" onClick={onOpen}>{open} &rarr;</button>
    </div>
  )
}

/**
 * Prima vedere a paginii: cate o carte pe zona, cu starea ei pe nivelul ales si
 * de unde vine (setat aici / din valorile pentru toate firmele / din fabrica).
 * Nu scrie nimic; fiecare carte duce la tab-ul unde se schimba.
 */
export function AutonomyOverview({ d, level, onOpen }: { d: Page; level: Level; onOpen(t: Tab): void }) {
  const k = d.desk
  const p = k.policy ?? {}
  const mail = useQuery(mailIntakeQuery(k.scope)).data?.setting ?? null
  const all = level === 'all'
  const source = (own: boolean, inherited: string) =>
    own ? 'set here' : all ? 'factory default' : inherited === 'provider' ? 'from the defaults for all companies' : 'factory default'

  const deskOn = Boolean(n(p.enabled))
  const deskPausedHere = Boolean(n(p.paused))
  const deskState = !deskOn ? ['Off', 'off'] : deskPausedHere ? ['Paused', 'bad'] : ['On', 'good']
  const today = k.today ?? {}

  const mailState: [string, Tone] = !mail ? ['Not set up', 'off'] : !mail.enabled ? ['Off', 'off'] : mail.mode === 'live' ? ['Live', 'good'] : ['Watching', 'info']
  const mailBroken = Boolean(mail?.enabled && mail.last_error)

  const askOn = p.ask_too === undefined ? true : Boolean(n(p.ask_too))
  const chatOn = Boolean(n(p.chat_too))

  const ownResponse = Object.values(d.response.matrix).filter((r) => !r.inherited).length + d.response.overrides.length
  const rules = all ? d.rules : d.rules.filter((r) => n(r.company_id) === 0 || n(r.company_id) === d.companyId)
  const override = (r: (typeof rules)[number]) => (all || n(r.company_id) !== 0 ? undefined : d.overrides[String(n(r.ID))])
  const liveRules = rules.filter((r) => n(r.enabled) === 1 && n(override(r)?.is_off) !== 1)
  const acting = liveRules.filter((r) => {
    const ov = override(r)
    const posture = ov && s(ov.posture) ? s(ov.posture) : s(r.posture)
    return posture !== 'shadow'
  }).length

  return (
    <div className="odx-cards" id="odx-overview">
      {!all && !k.aiReady && (
        <Card id="odx-card-ai" title="AI agent" state="Not configured" tone="bad" alert open="Settings → AI Agent" onOpen={() => { window.location.href = '/app/settings/ai_agent' }}
          facts={['The desk can be switched on, but nothing answers until this company has a model.']} />
      )}
      <Card id="odx-card-desk" title="Service desk" state={deskState[0]} tone={deskState[1] as Tone} open="Set up the desk" onOpen={() => onOpen('desk')}
        facts={[
          <span key="src" className={k.own ? 'odx-inherit is-own' : 'odx-inherit'}>{source(k.own, s(p.inherited))}</span>,
          deskOn ? <>Goes as far as: <strong>{s(p.posture) === 'act' ? 'act on the machine' : 'read and answer'}</strong></> : null,
          (n(today.taken) > 0 || n(today.handed) > 0) ? <>Today: {n(today.taken)} taken, {n(today.handed)} handed over</> : null,
        ]}>
        <p>The AI agent answers tickets from people, and fixes what it is allowed to.</p>
      </Card>
      <Card id="odx-card-mail" title="Email intake" state={mailState[0]} tone={mailBroken ? 'bad' : mailState[1]} alert={mailBroken} open={mail ? 'Open the mailbox' : 'Connect a mailbox'} onOpen={() => onOpen('channels')}
        facts={[
          mail ? <>Mailbox: <strong>{mail.address}</strong></> : null,
          mailBroken ? <span style={{ color: '#b42318' }}>Last read failed: {mail?.last_error}</span> : null,
        ]}>
        <p>{all ? 'The provider mailbox, for every company without one of its own.' : 'Emails to this mailbox become tickets the desk answers.'}</p>
      </Card>
      <Card id="odx-card-ask" title="Customer app and chat" state={askOn || chatOn ? 'On' : 'Off'} tone={askOn || chatOn ? 'good' : 'off'} open="Open the channels" onOpen={() => onOpen('channels')}
        facts={[
          <>Customer app: <strong>{askOn ? (s(p.ask_mode) === 'live' ? 'live conversation' : 'becomes a ticket') : 'off'}</strong></>,
          <>Station chat to the desk: <strong>{chatOn ? 'yes' : 'no'}</strong></>,
        ]}>
        <p>Requests from ask.optimumdesk.ai and from the chat on the station.</p>
      </Card>
      <Card id="odx-card-alarms" title="Alarm response" state={ownResponse > 0 ? `${ownResponse} set here` : all ? 'Factory default' : 'Inherited'} tone={ownResponse > 0 ? 'info' : 'off'} open="Open alarms" onOpen={() => onOpen('alarms')}
        facts={[n(d.response.today?.responses) > 0 ? <>Today: {n(d.response.today.responses)} alarm(s) answered, {n(d.response.today.tickets)} ticket(s)</> : null]}>
        <p>What happens the minute an alarm opens: who is told, whether a ticket opens, whether the agent takes it.</p>
      </Card>
      <Card id="odx-card-rules" title="Rules" state={d.switchedOn ? `${liveRules.length} on` : 'Master switch OFF'} tone={!d.switchedOn ? 'warn' : liveRules.length > 0 ? 'good' : 'off'} open="Open rules" onOpen={() => onOpen('alarms')}
        facts={[
          <>{rules.length} rule{rules.length === 1 ? '' : 's'}{all ? '' : ' that apply here'}, {acting} beyond shadow</>,
        ]}>
        <p>A pattern in the log tied to a pre-approved action, in shadow, propose or remediate.</p>
      </Card>
      <Card id="odx-card-texts" title="Texts and metrics" state="" tone="off" open="Open texts" onOpen={() => onOpen('texts')}
        facts={[<button key="m" type="button" className="odx-card-open" style={{ marginTop: 0 }} onClick={() => onOpen('metrics')}>See the metrics &rarr;</button>]}>
        <p>The emails and pages the desk sends, in each language; and how the desk is doing.</p>
      </Card>
    </div>
  )
}
